/**
 * Solicitacoes do cliente (spec 007, secao 7; spec 038, secao 3): liberacao e pedidos de analise. Cada pedido mostra o
 * caminho dele (pedido, aprovado, pago, liberado) com as datas que o backend mandou. Em andamento ficam em cima; as
 * decididas mostram as 3 ultimas e o resto abre em "Ver todas". Avanco aprovado com mensalidade antecipada fica
 * "aguardando pagamento": aqui aparece o valor e os dois jeitos de pagar, a Pagar.me (PIX ou cartao) e o PIX direto com o
 * envio do comprovante (spec 047). A aprovacao do Challenge da Global (spec 064) segue o mesmo caminho, com a taxa de
 * ativacao no lugar da mensalidade antecipada; os textos de cada tipo moram em `PEDIDOS_COM_PAGAMENTO`.
 */
import { Aviso, Botao, CabecalhoDePagina, EsqueletoDeCartao, formatarData, formatarDataHora, formatarDia, formatarReais, LinkInterno, mensagemDe, Paginacao, paginar, Percurso, Pilha, POR_PAGINA, Secao, Selo, Vazio, type PassoDoPercurso } from "@mtc/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDentroDeAssunto } from "../assunto";
import { useFiltroDePlano } from "../planoAtual";
import { useSessao } from "../sessao";
import { ABERTAS, PEDIDOS_COM_PAGAMENTO, STATUS_DA_SOLICITACAO, type Solicitacao } from "../tipos";
import { useSolicitacoes } from "./Planos";
import { PagarCobranca, useComprovantes } from "./comprovantes";

/** Quantas decididas aparecem antes do "Ver todas". */
const DECIDIDAS_A_VISTA = 3;

/** Em andamento pro cliente: em analise, esperando pagamento, ou aprovado esperando a conta nova. */
const emAndamento = (s: Solicitacao) => ABERTAS.includes(s.status) || (s.status === "APROVADA" && Boolean(s.aguardando_liberacao));

export function PaginaSolicitacoes() {
  const { id } = useParams();
  const q = useSolicitacoes();
  const filtro = useFiltroDePlano();
  const dentroDeAssunto = useDentroDeAssunto();
  const [todas, setTodas] = useState(false);
  // O texto acompanha o pedido que o ambiente tem: a aprovacao do Challenge (nelogica-v2) ou a liberacao e a analise.
  const { estado } = useSessao();
  const daAprovacao = estado.status === "logado" && estado.usuario.capacidades.modulos.includes("nelogica-v2");
  const itens = (q.data?.itens ?? []).filter((s) => !filtro.matriculaId || s.matricula.id === filtro.matriculaId);
  const abertas = itens.filter(emAndamento);
  const decididas = itens.filter((s) => !emAndamento(s));
  // Link direto pra um pedido antigo (e-mail, sino): ele aparece mesmo fora das 3 ultimas.
  const pedidoEscondido = Boolean(id && decididas.findIndex((s) => s.id === id) >= DECIDIDAS_A_VISTA);
  // "Ver todas" vem de 20 em 20 (spec 058); o link direto pra um pedido abre na pagina dele.
  const paginaDoPedido = id ? Math.floor(Math.max(0, decididas.findIndex((s) => s.id === id)) / POR_PAGINA) + 1 : 1;
  const [pagina, setPagina] = useState<number | null>(null);
  const paginaAtual = pagina ?? (pedidoEscondido ? paginaDoPedido : 1);
  const aVista = todas || pedidoEscondido ? paginar(decididas, paginaAtual) : decididas.slice(0, DECIDIDAS_A_VISTA);
  useEffect(() => {
    if (id) document.getElementById(`solicitacao-${id}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [id, q.data]);
  const cabecalho = !dentroDeAssunto && <CabecalhoDePagina titulo="Solicitações" descricao={daAprovacao ? "Seus pedidos de aprovação do Challenge. Quando a aprovação pede a taxa de ativação, você paga aqui, por PIX ou cartão ou pelo PIX direto com o comprovante." : "Seus pedidos: liberação da plataforma e análise do resultado. Quando um avanço pede pagamento, você paga aqui, por PIX ou cartão ou pelo PIX direto com o comprovante."} />;
  if (q.isLoading) return <Pilha espaco={6}>{cabecalho}<EsqueletoDeCartao linhas={2} barra rotulo="Buscando suas solicitações" /><EsqueletoDeCartao linhas={2} barra /></Pilha>;
  if (q.isError) return <Aviso tipo="erro">{mensagemDe(q.error, "Não conseguimos carregar suas solicitações agora. Recarregue a página.")}</Aviso>;
  return (
    <Pilha espaco={6}>
      {cabecalho}
      {!itens.length ? (
        <Vazio titulo="Nenhuma solicitação">{daAprovacao ? "Quando você pedir a aprovação do Challenge, o pedido aparece aqui." : "Quando você agendar o início ou pedir a análise do resultado, o pedido aparece aqui."}</Vazio>
      ) : (
        <>
          <Pilha espaco={4}>
            <h2 className="mtc-rotulo" style={{ margin: 0 }}>Em andamento ({abertas.length})</h2>
            {abertas.length ? abertas.map((s) => <Pedido key={s.id} s={s} destaque={s.id === id} />) : <p className="mtc-texto-2" style={{ margin: 0 }}>Nada em andamento.</p>}
          </Pilha>
          {decididas.length > 0 && (
            <Pilha espaco={4}>
              <h2 className="mtc-rotulo" style={{ margin: 0 }}>Decididas ({decididas.length})</h2>
              {aVista.map((s) => <Pedido key={s.id} s={s} destaque={s.id === id} />)}
              {(todas || pedidoEscondido) && <Paginacao pagina={paginaAtual} total={decididas.length} aoMudar={setPagina} rotulo="Páginas das solicitações decididas" />}
              {decididas.length > DECIDIDAS_A_VISTA && !pedidoEscondido && (
                <div><Botao variante="secundario" onClick={() => setTodas(!todas)}>{todas ? "Mostrar só as 3 últimas" : `Ver todas (${decididas.length})`}</Botao></div>
              )}
            </Pilha>
          )}
        </>
      )}
    </Pilha>
  );
}

/**
 * O caminho de um pedido, com as datas que o backend tem. Liberacao: pedido e liberado. Avanco: pedido, aprovado,
 * pago (so quando tem mensalidade antecipada) e liberado. Recusado, cancelado e revertido fecham o caminho em vermelho.
 */
function caminhoDo(s: Solicitacao): PassoDoPercurso[] {
  const pedido: PassoDoPercurso = { chave: "pedido", titulo: "Pedido", quando: formatarDataHora(s.criada_em), estado: "feito" };
  const decidido = s.decidida_em ? formatarDataHora(s.decidida_em) : undefined;
  if (s.status === "RECUSADA") return [pedido, { chave: "fim", titulo: "Não aprovado", quando: decidido, estado: "erro" }];
  if (s.status === "CANCELADA") return [pedido, { chave: "fim", titulo: "Cancelado", quando: decidido, estado: "erro" }];
  if (!PEDIDOS_COM_PAGAMENTO[s.tipo]) {
    if (s.status === "PENDENTE") return [pedido, { chave: "fim", titulo: s.tipo === "LIBERACAO" ? "Liberação" : "Decisão da equipe", detalhe: "A equipe confere e decide", estado: "atual" }];
    return [pedido, { chave: "fim", titulo: s.tipo === "LIBERACAO" ? "Liberado" : STATUS_DA_SOLICITACAO[s.status].rotulo, quando: decidido, estado: s.status === "REVOGADA" ? "erro" : "feito" }];
  }
  const temPortao = s.valor_centavos != null;
  const aprovado: PassoDoPercurso = s.status === "PENDENTE"
    ? { chave: "aprovado", titulo: "Aprovado", detalhe: "A equipe confere o resultado", estado: "atual" }
    : { chave: "aprovado", titulo: "Aprovado", quando: decidido, estado: "feito" };
  const pago: PassoDoPercurso | null = !temPortao ? null
    : s.pagamento ? { chave: "pago", titulo: "Pago", quando: `Pago em ${formatarDataHora(s.pagamento.pago_em)}`, detalhe: `${formatarReais(s.valor_centavos!)}${s.pagamento.como === "POR_FORA" ? ", conferido por um administrador" : s.pagamento.meio ? `, ${s.pagamento.meio === "pix" ? "PIX" : "cartão"}` : ""}`, estado: "feito" }
    : { chave: "pago", titulo: "Pagamento", detalhe: formatarReais(s.valor_centavos!), estado: s.status === "AGUARDANDO_PAGAMENTO" ? "atual" : "futuro" };
  const liberado: PassoDoPercurso = s.status === "REVOGADA"
    ? { chave: "liberado", titulo: "Revertido", quando: decidido, estado: "erro" }
    : s.status === "APROVADA" && !s.aguardando_liberacao
      ? { chave: "liberado", titulo: "Liberado", quando: s.liberada_no_dia ? `Em ${formatarDia(s.liberada_no_dia)}` : undefined, detalhe: "Conta nova pronta", estado: "feito" }
      : { chave: "liberado", titulo: "Liberado", detalhe: s.status === "APROVADA" ? `Conta pronta em até ${s.prazo ?? "o prazo combinado"}` : undefined, estado: s.status === "APROVADA" ? "espera" : "futuro" };
  return [pedido, aprovado, ...(pago ? [pago] : []), liberado];
}

function Pedido({ s, destaque }: { s: Solicitacao; destaque: boolean }) {
  const st = STATUS_DA_SOLICITACAO[s.status];
  const esperandoConta = s.status === "APROVADA" && s.aguardando_liberacao;
  const t = PEDIDOS_COM_PAGAMENTO[s.tipo];
  return (
    <div id={`solicitacao-${s.id}`}>
      <Secao destaque={destaque || s.status === "AGUARDANDO_PAGAMENTO"} rotulo={s.matricula.plano.nome} titulo={s.titulo} acoes={<Selo tom={esperandoConta ? "info" : st.tom}>{esperandoConta ? "Aprovada, conta a caminho" : st.rotulo}</Selo>}>
        <p style={{ margin: 0 }}>{s.resumo}</p>
        <Percurso rotulo={`Andamento do pedido de ${formatarData(s.criada_em)}`} passos={caminhoDo(s)} />
        {s.mensagem && <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{t ? "Sua mensagem" : "Detalhe"}: {s.mensagem}</p>}
        {s.observacao && <Aviso tipo={s.status === "RECUSADA" || s.status === "REVOGADA" ? "alerta" : "info"}>Observação da equipe: {s.observacao}</Aviso>}
        {s.status === "PENDENTE" && t?.emAnalise && <Aviso tipo="alerta">{t.emAnalise}</Aviso>}
        {s.status === "AGUARDANDO_PAGAMENTO" && <PagarPortao s={s} />}
        {esperandoConta && (
          <Aviso tipo="info">{t?.aprovado ?? "Avanço aprovado"}. A liberação {t?.daConta ?? "da nova conta"} ocorre em até {s.prazo ?? "o prazo combinado"}, e você é avisado por e-mail quando ela estiver pronta.</Aviso>
        )}
        {s.status === "APROVADA" && t && !s.aguardando_liberacao && <LinkInterno href={`/planos/${s.matricula.id}`}>{t.verPlano}</LinkInterno>}
      </Secao>
    </div>
  );
}

/**
 * O portao do avanco (spec 007, M7; spec 047): paga pela Pagar.me (PIX ou cartao, baixa na hora) ou pelo PIX direto,
 * com o comprovante aqui mesmo, que um administrador confere. O avanco segue quando ele declarar a conta.
 */
function PagarPortao({ s }: { s: Solicitacao }) {
  const t = PEDIDOS_COM_PAGAMENTO[s.tipo] ?? PEDIDOS_COM_PAGAMENTO.AVANCO_ETAPA!;
  const comprovantes = useComprovantes(!s.pagamento);
  // Com o comprovante em analise, o pedido nao pede pagar de novo: o "em analise" com o prazo e que fala.
  const emAnalise = Boolean(comprovantes.data?.itens.some((c) => c.alvo.tipo === "PEDIDO" && c.alvo.id === s.id && c.estado === "EM_ANALISE"));
  if (s.pagamento) {
    return <Aviso tipo="sucesso">Pagamento recebido{s.pagamento.como === "PLATAFORMA" && s.pagamento.meio ? ` (${s.pagamento.meio === "pix" ? "PIX" : "cartão"})` : ""} em {formatarDataHora(s.pagamento.pago_em)}. {t.recebido} e você recebe o aviso aqui e por e-mail.</Aviso>;
  }
  const valor = s.valor_centavos != null ? formatarReais(s.valor_centavos) : t.oQueSePaga;
  return (
    <Pilha espaco={3}>
      {!emAnalise && <Aviso tipo="alerta">Pague <strong>{valor}</strong> {t.pague}: por PIX ou cartão, com confirmação na hora, ou pelo PIX direto, com o comprovante, que um administrador confere em até 1 dia.</Aviso>}
      <PagarCobranca tipo="PEDIDO" id={s.id} valorCentavos={s.valor_centavos} podePagar={s.pode_pagar} />
    </Pilha>
  );
}
